import React, { useState } from 'react';

import { ArrowLeft, Pencil } from 'lucide-react';

import { CATEGORY_COLOR, CATEGORY_LABEL, NO_CATEGORY_LABEL } from '../constants';
import { FeatureCatalogEntry } from '../types';
import { launchBadgeLabel } from '../utils';

import FeatureEditForm from './FeatureEditForm';
import FeatureStatusControl from './FeatureStatusControl';

const NEUTRAL_COLOR = '#6B7280';

export const categoryChipStyle = (color: string): React.CSSProperties => ({
  fontSize: 11.5,
  fontWeight: 600,
  lineHeight: 1,
  color,
  background: `${color}1a`,
  border: `1px solid ${color}40`,
  padding: '6px 10px',
  borderRadius: 999,
  whiteSpace: 'nowrap',
});

export const entryCategoryColor = (entry: FeatureCatalogEntry): string =>
  entry.category ? CATEGORY_COLOR[entry.category] : NEUTRAL_COLOR;

export const entryCategoryLabel = (entry: FeatureCatalogEntry): string =>
  entry.category ? CATEGORY_LABEL[entry.category] : NO_CATEGORY_LABEL;

const LEGACY_COLOR = '#B45309';

// "Legacy" mark and, for features from before 2026, their month and year with the
// "Antes de 2026" mark.
export const FeatureBadges: React.FC<{ entry: FeatureCatalogEntry }> = ({ entry }) => {
  const launch = launchBadgeLabel(entry);
  return (
    <>
      {entry.status === 'legacy' && (
        <span style={{ ...categoryChipStyle(LEGACY_COLOR), padding: '3px 9px' }}>Legacy</span>
      )}
      {launch && (
        <span style={{ ...categoryChipStyle(NEUTRAL_COLOR), padding: '3px 9px' }}>{launch}</span>
      )}
    </>
  );
};

const SECTION_LABEL_STYLE: React.CSSProperties = {
  fontSize: 11,
  fontWeight: 700,
  letterSpacing: '.08em',
  textTransform: 'uppercase',
  color: NEUTRAL_COLOR,
  marginBottom: 4,
};

type FeatureDetailProps = {
  entry: FeatureCatalogEntry;
  related: FeatureCatalogEntry[];
  onBack: () => void;
  onSelect: (entryId: number) => void;
};

const FeatureDetail: React.FC<FeatureDetailProps> = ({ entry, related, onBack, onSelect }) => {
  const [isEditing, setIsEditing] = useState(false);
  const color = entryCategoryColor(entry);

  return (
    <div
      className="flex flex-col h-full overflow-hidden"
      style={{ animation: 'dgFade 300ms cubic-bezier(.2,.7,.3,1) both' }}
    >
      <div className="flex-shrink-0 px-6 pt-5 pb-3">
        <button
          type="button"
          onClick={onBack}
          style={{
            height: 36,
            padding: '0 14px',
            border: '1px solid #D8DADF',
            background: '#fff',
            borderRadius: 999,
            font: "600 13.5px/1 'Montserrat',sans-serif",
            color: '#2E4774',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: 6,
          }}
        >
          <ArrowLeft size={14} />
          Lanzamientos
        </button>
      </div>

      <div className="flex-1 px-6 pb-10 overflow-y-auto">
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit,minmax(min(100%,520px),1fr))',
            gap: 22,
            alignItems: 'start',
          }}
        >
          {isEditing ? (
            <FeatureEditForm
              entry={entry}
              onCancel={() => setIsEditing(false)}
              onSaved={() => setIsEditing(false)}
            />
          ) : (
            <div className="dg-card" style={{ borderRadius: 16, padding: '30px 32px 34px' }}>
              <div className="flex flex-wrap items-center gap-2" style={{ marginBottom: 14 }}>
                <span style={categoryChipStyle(color)}>{entryCategoryLabel(entry)}</span>
                <FeatureBadges entry={entry} />
                <button
                  type="button"
                  className="flex items-center gap-1 ml-auto text-sm font-semibold text-blue-800"
                  onClick={() => setIsEditing(true)}
                >
                  <Pencil size={13} />
                  Editar
                </button>
              </div>

              <h1
                style={{
                  fontWeight: 800,
                  fontSize: 'clamp(25px,2.4vw,34px)',
                  lineHeight: 1.16,
                  color: '#111A2B',
                  letterSpacing: '-.03em',
                  marginBottom: 20,
                }}
              >
                {entry.title}
              </h1>

              <FeatureStatusControl key={entry.status} entry={entry} />

              {entry.customer_benefit && (
                <div style={{ marginBottom: 20 }}>
                  <h3
                    style={{
                      font: "700 15px/1.3 'Montserrat',sans-serif",
                      color: '#2E4774',
                      marginBottom: 10,
                    }}
                  >
                    Beneficio para el cliente
                  </h3>
                  <div
                    style={{
                      background: '#EBF1FA',
                      border: '1px solid #D6E2F3',
                      borderRadius: 12,
                      padding: '18px 20px',
                    }}
                  >
                    <p style={{ fontSize: 14.5, lineHeight: 1.6, color: '#1E3460' }}>
                      {entry.customer_benefit}
                    </p>
                  </div>
                </div>
              )}

              <div
                style={{
                  borderRadius: 12,
                  background: '#F3F6FA',
                  border: '1px solid #E1E7F3',
                  padding: '14px 16px',
                  marginBottom: 16,
                }}
              >
                <p style={SECTION_LABEL_STYLE}>Área</p>
                <p style={{ fontSize: 14, fontWeight: 600, color: '#111A2B' }}>
                  {entryCategoryLabel(entry)}
                </p>
                {entry.tags.length > 0 && (
                  <div className="flex flex-wrap gap-1.5 mt-2">
                    {entry.tags.map((tag) => (
                      <span
                        key={tag}
                        style={{
                          ...categoryChipStyle(NEUTRAL_COLOR),
                          fontSize: 11,
                          padding: '3px 8px',
                        }}
                      >
                        {tag}
                      </span>
                    ))}
                  </div>
                )}
              </div>

              <div
                style={{
                  borderRadius: 12,
                  background: '#FFF6E6',
                  border: '1px solid #FFE6BD',
                  padding: '14px 16px',
                  marginBottom: 16,
                }}
              >
                <p style={{ ...SECTION_LABEL_STYLE, color: '#8A4F0D', marginBottom: 6 }}>
                  ⚙ Configuración en Telescope
                </p>
                {entry.staff_setup_guide ? (
                  <p
                    style={{
                      fontSize: 13.5,
                      color: '#6B3A0F',
                      lineHeight: 1.6,
                      whiteSpace: 'pre-wrap',
                    }}
                  >
                    {entry.staff_setup_guide}
                  </p>
                ) : (
                  <p style={{ fontSize: 13.5, color: '#6B3A0F' }}>Pendiente de artículo</p>
                )}
              </div>

              {entry.support_article_url && (
                <a
                  href={entry.support_article_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ fontSize: 13.5, fontWeight: 600, color: '#2E4774' }}
                >
                  Ver artículo en el Centro de Ayuda
                </a>
              )}
            </div>
          )}

          {entry.category && (
            <div className="dg-card" style={{ borderRadius: 16, padding: 20 }}>
              <div
                style={{
                  font: "700 11px/1.2 'Montserrat',sans-serif",
                  letterSpacing: '.08em',
                  textTransform: 'uppercase',
                  color: NEUTRAL_COLOR,
                  marginBottom: 14,
                }}
              >
                Más de {entryCategoryLabel(entry)}
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                {related.length === 0 ? (
                  <p style={{ fontSize: 13.5, color: NEUTRAL_COLOR }}>
                    Es la primera funcionalidad de esta categoría en el catálogo.
                  </p>
                ) : (
                  related.map((r) => (
                    <button
                      key={r.id}
                      type="button"
                      className="dg-related-item text-left transition-colors"
                      style={{ borderRadius: 12, padding: '14px 16px', cursor: 'pointer' }}
                      onClick={() => onSelect(r.id)}
                    >
                      <div
                        style={{
                          fontSize: 14.5,
                          fontWeight: 600,
                          color: '#111A2B',
                          lineHeight: 1.35,
                        }}
                      >
                        {r.title}
                      </div>
                    </button>
                  ))
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default FeatureDetail;
